import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const HEARTBEAT_INTERVAL = 60000; // 1 minute

/**
 * Hook for tracking who else is viewing/editing an entity
 * Provides real-time presence indicators and handles optimistic locking
 */
export const usePresence = (entityType, entityId, options = {}) => {
  const { api, user } = useAuth();
  const [othersViewing, setOthersViewing] = useState([]);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);
  const { enabled = true } = options;

  // Register presence when component mounts
  const registerPresence = useCallback(async () => {
    if (!entityType || !entityId || !enabled) return;
    
    try {
      const response = await api.post(`/presence/${entityType}/${entityId}`);
      setOthersViewing(response.data.others_viewing || []);
    } catch (error) {
      console.error('Failed to register presence:', error);
    } finally {
      setLoading(false);
    }
  }, [api, entityType, entityId, enabled]);

  // Remove presence when component unmounts
  const removePresence = useCallback(async () => {
    if (!entityType || !entityId || !enabled) return;
    
    try {
      await api.delete(`/presence/${entityType}/${entityId}`);
    } catch (error) {
      console.error('Failed to remove presence:', error);
    }
  }, [api, entityType, entityId, enabled]);

  // Refresh presence (heartbeat)
  const refreshPresence = useCallback(async () => {
    if (!entityType || !entityId || !enabled) return;
    
    try {
      const response = await api.post(`/presence/${entityType}/${entityId}`);
      setOthersViewing(response.data.others_viewing || []);
    } catch (error) {
      console.error('Failed to refresh presence:', error);
    }
  }, [api, entityType, entityId, enabled]);

  // Check presence without registering
  const checkPresence = useCallback(async () => {
    if (!entityType || !entityId || !enabled) return [];
    
    try {
      const response = await api.get(`/presence/${entityType}/${entityId}`);
      setOthersViewing(response.data.others_viewing || []);
      return response.data.others_viewing || [];
    } catch (error) {
      console.error('Failed to check presence:', error);
      return [];
    }
  }, [api, entityType, entityId, enabled]);

  useEffect(() => {
    if (!enabled || !entityType || !entityId) {
      setLoading(false);
      return;
    }

    // Register presence on mount
    registerPresence();

    // Set up heartbeat
    heartbeatRef.current = setInterval(refreshPresence, HEARTBEAT_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      removePresence();
    };
  }, [entityType, entityId, enabled, registerPresence, refreshPresence, removePresence]);

  return {
    othersViewing,
    loading,
    isBeingViewed: othersViewing.length > 0,
    refreshPresence,
    checkPresence,
    removePresence
  };
};

/**
 * Helper to handle version conflict errors from API
 */
export const isVersionConflict = (error) => {
  if (!error?.response) return false;
  const { status, data } = error.response;
  return status === 409 && data?.error === 'version_conflict';
};

/**
 * Get conflict details from error
 */
export const getConflictDetails = (error) => {
  if (!isVersionConflict(error)) return null;
  return error.response.data;
};

export default usePresence;
